import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	dxtToLocalServerTime,
	ErrorModal,
	MesfModal,
} from "@dexteel/mesf-core";
import {
	Button,
	Card,
	CardContent,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { CommentsField } from "@/shared/components/CommentsField";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { IncentiveRow } from "../models/IncentiveRow";
import { useUpdateShiftOverride } from "../hooks/useUpdateShiftOverride";

type Props = {
	row: IncentiveRow | null;
	onHide: () => void;
};

type FormValues = {
	targetTonsOverride: number | "";
	adjustment: number | "";
	notes: string;
};

export const EditShiftOverrideModal = ({ row, onHide }: Props) => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const mutation = useUpdateShiftOverride();

	const {
		control,
		reset,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<FormValues>({
		defaultValues: {
			targetTonsOverride: "",
			adjustment: 0,
			notes: "",
		},
	});

	const shiftDisplay = row?.Shift ?? "";
	const dateDisplay = row
		? String(row.ProductionDate)
				.substring(0, 10)
				.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1")
		: "";

	const modalTitle = row
		? `EDIT INCENTIVE — ${dateDisplay}, ${shiftDisplay} (Crew ${row.Crew})`
		: "EDIT INCENTIVE";

	// Sync form state when a different row is opened
	useEffect(() => {
		if (row) {
			reset({
				targetTonsOverride: row.TargetTonsOverride ?? "",
				adjustment: row.Adjustment,
				notes: row.Notes ?? "",
			});
			setNotificationMessage("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [row?.ShiftId]);

	const onSubmit = (data: FormValues) => {
		if (!row) return;

		mutation.mutate(
			{
				ShiftId: row.ShiftId,
				TargetTonsOverride:
					data.targetTonsOverride === "" || data.targetTonsOverride === 0
						? null
						: data.targetTonsOverride,
				Adjustment: data.adjustment === "" ? 0 : data.adjustment,
				Notes: data.notes.trim() || null,
			},
			{
				onSuccess: () => {
					setNotificationMessage("Shift override saved successfully");
					onHide();
				},
				onError: (err: unknown) => {
					setError(
						err instanceof Error ? err.message : "Error saving override",
					);
				},
			},
		);
	};

	return (
		<>
			<MesfModal
				title={modalTitle}
				open={row !== null}
				id="edit-shift-override-modal"
				handleClose={() => onHide()}
				maxWidth="sm"
			>
				<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2}>
						{/* Audit Card */}
						<Grid2 size={{ xs: 12 }}>
							<Card variant="outlined">
								<CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
									<Typography
										variant="subtitle2"
										sx={{ mb: 1, fontWeight: 600 }}
									>
										Shift Details
									</Typography>
									<Grid2 container spacing={1} justifyContent="center">
										<Grid2 size={{ xs: 4 }} sx={{ textAlign: "center" }}>
											<Typography variant="caption" color="text.secondary">
												Original Target
											</Typography>
											<Typography variant="body2">
												{row?.TargetTons != null
													? row.TargetTons.toFixed(0)
													: "—"}
											</Typography>
										</Grid2>
										<Grid2 size={{ xs: 4 }} sx={{ textAlign: "center" }}>
											<Typography variant="caption" color="text.secondary">
												Last Override By
											</Typography>
											<Typography variant="body2">
												{row?.LastOverrideBy ?? "—"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{row?.LastOverrideAt
													? dxtToLocalServerTime(
															row.LastOverrideAt,
															"MM/dd/yy HH:mm",
														)
													: ""}
											</Typography>
										</Grid2>
										<Grid2 size={{ xs: 4 }} sx={{ textAlign: "center" }}>
											<Typography variant="caption" color="text.secondary">
												Last Adjustment By
											</Typography>
											<Typography variant="body2">
												{row?.LastAdjustmentBy ?? "—"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{row?.LastAdjustmentAt
													? dxtToLocalServerTime(
															row.LastAdjustmentAt,
															"MM/dd/yy HH:mm",
														)
													: ""}
											</Typography>
										</Grid2>
									</Grid2>
								</CardContent>
							</Card>
						</Grid2>

						{/* Editable Fields */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="targetTonsOverride"
								control={control}
								rules={{
									validate: (v) => {
										if (v === "") return true;
										if (Number(v) < 0) return "Cannot be negative";
										return true;
									},
								}}
								render={({ field }) => (
									<TextField
										label="Target Tons Override"
										type="number"
										fullWidth
										value={field.value}
										onChange={(e) => {
											if (e.target.value === "") {
												field.onChange("");
												return;
											}
											const val = Number.parseFloat(e.target.value);
											if (!Number.isNaN(val)) {
												field.onChange(val);
											}
										}}
										onBlur={field.onBlur}
										error={!!errors.targetTonsOverride}
										helperText={
											errors.targetTonsOverride?.message ??
											"Clear to restore original target"
										}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="adjustment"
								control={control}
								rules={{
									validate: (v) => {
										if (v === "" || v === 0) return true;
										const val = Number(v);
										const finished = row?.FinishedTons;
										const target = row?.TargetTons;
										if (val > 0) {
											if (target != null && val > target)
												return `Cannot exceed target tons (${target.toFixed(0)})`;
											if (finished != null && val > finished)
												return `Cannot exceed finished tons (${finished.toFixed(0)})`;
										}
										if (val < 0 && finished != null && Math.abs(val) > finished)
											return `Absolute value cannot exceed finished tons (${finished.toFixed(0)})`;
										return true;
									},
								}}
								render={({ field }) => (
									<TextField
										label="Adjustment"
										type="number"
										fullWidth
										value={field.value}
										onChange={(e) => {
											if (e.target.value === "") {
												field.onChange("");
												return;
											}
											const val = Number.parseFloat(e.target.value);
											if (!Number.isNaN(val)) {
												field.onChange(val);
											}
										}}
										onBlur={field.onBlur}
										error={!!errors.adjustment}
										helperText={errors.adjustment?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="notes"
								control={control}
								render={({ field }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										rows={{ minRows: 2, maxRows: 4 }}
										maxLength={500}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="text"
								onClick={() => onHide()}
								disabled={mutation.isLoading}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								disabled={mutation.isLoading || !row || !isDirty}
								onClick={handleSubmit(onSubmit)}
							>
								{mutation.isLoading ? "Saving..." : "Save"}
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
